import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import { formatMoney } from '../ui/format'
import { Banner, Empty, ErrorBanner, Panel, Spinner } from '@shop/ui'

type PartyKind = 'EMPLOYEE' | 'SUPPLIER'

type Payee = { id: string; name: string }

type PartyBalance = {
  partyKind: PartyKind
  partyId: string
  accountCode: string
  /** موجبٌ = علينا له · سالبٌ = دفعنا أكثرَ ممّا استحقّ (سلفة). */
  balance: number
  lastEntry: string
}


type AccountLedger = { closing: number }

/**
 * الجمعيّة — <b>كما قالها المالك ٢٠٢٦-١٠-٠٧</b>: ٦٦٠٠ دينار، القسطُ ٣٠٠ شهريًّا.
 * والمدفوعُ يُقرأ من الدفتر (<c>1300</c>)، والأصلُ رقمٌ ثابتٌ حتّى تنتهي الجمعيّة.
 */
const JAMIYA_TOTAL = 6600
const JAMIYA_MONTHLY = 300

/**
 * الأرصدة — <b>ما علينا لكلّ مورّدٍ وموظّف</b>، والجمعيّة (<c>0048</c>).
 *
 * ⚠ <b>وكلُّ رقمٍ هنا مجموعٌ من الدفتر</b> — لا عمودَ رصيدٍ يُحدَّث. فدفعةٌ من
 *   الدرج في نقطة البيع، أو من «مصاريفُ ودفعات»، تظهر هنا فورًا.
 */
export default function Balances() {
  const { can } = useAuth()
  const navigate = useNavigate()

  // الاسمُ يُضغط فيفتح كشفَه في صفحة «كشفُ حساب» — طلبُ المالك ٢٠٢٦-١٠-٠٨.
  const open = (party: { kind: PartyKind; id: string }) =>
    navigate(`/statement?tab=${party.kind === 'SUPPLIER' ? 'suppliers' : 'employees'}&id=${party.id}`)

  const names = useQuery({
    queryKey: ['payout-options'],
    queryFn: () => api<{ employees: Payee[]; suppliers: Payee[] }>('/api/v1/cash-payouts/options'),
  })

  const suppliers = useQuery({
    queryKey: ['balances', 'SUPPLIER'],
    queryFn: () => api<PartyBalance[]>('/api/v1/accounting/parties?kind=SUPPLIER'),
  })

  const employees = useQuery({
    queryKey: ['balances', 'EMPLOYEE'],
    queryFn: () => api<PartyBalance[]>('/api/v1/accounting/parties?kind=EMPLOYEE'),
  })

  const jamiya = useQuery({
    queryKey: ['balances', 'jamiya'],
    queryFn: () => api<AccountLedger>('/api/v1/accounting/accounts/1300/ledger?from=2000-01-01'),
  })


  if (!can(Perm.ledgerView)) {
    return (
      <Panel title="الأرصدة">
        <Banner tone="warn">هذه الشاشةُ تحتاج صلاحيّةَ «عرضُ الدفاتر».</Banner>
      </Panel>
    )
  }

  const paid = jamiya.data?.closing ?? 0

  return (
    <div className="grid gap-4">
      <ErrorBanner error={names.error ?? suppliers.error ?? employees.error ?? jamiya.error} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Board
          title="المورّدون — ما علينا"
          people={names.data?.suppliers ?? []}
          balances={suppliers.data}
          loading={suppliers.isPending || names.isPending}
          kind="SUPPLIER"
          onOpen={open}
        />

        <Board
          title="الموظّفون — رواتبُ وعمولاتٌ مستحقّة"
          people={names.data?.employees ?? []}
          balances={employees.data}
          loading={employees.isPending || names.isPending}
          kind="EMPLOYEE"
          onOpen={open}
        />
      </div>

      <Panel title="الجمعيّة">
        {jamiya.isPending ? (
          <Spinner />
        ) : (
          <div className="grid gap-2 sm:grid-cols-3">
            <Stat label="المدفوع" value={formatMoney(paid)} />
            <Stat label="الباقي" value={formatMoney(Math.max(JAMIYA_TOTAL - paid, 0))} />
            <Stat
              label="أقساطٌ باقية"
              value={`${Math.ceil(Math.max(JAMIYA_TOTAL - paid, 0) / JAMIYA_MONTHLY)} × ${JAMIYA_MONTHLY}`}
            />
          </div>
        )}
        <p className="mt-2 text-xs text-ink-soft">
          من أصل {formatMoney(JAMIYA_TOTAL)} — مالُك محبوسٌ حتّى دورك، لذلك لا يُحسب مصروفًا.
        </p>
      </Panel>

    </div>
  )
}

function Board({
  title, people, balances, loading, kind, onOpen,
}: {
  title: string
  people: Payee[]
  balances: PartyBalance[] | undefined
  loading: boolean
  kind: PartyKind
  onOpen: (party: { kind: PartyKind; id: string }) => void
}) {
  const byId = new Map((balances ?? []).map((b) => [b.partyId, b.balance]))
  const total = (balances ?? []).reduce((sum, b) => sum + b.balance, 0)

  // كلُّ اسمٍ يُعرض ولو بصفر: «أبو آدم: صفر» خبرٌ، وغيابُ السطر سؤال.
  const rows = people
    .map((p) => ({ ...p, balance: byId.get(p.id) ?? 0 }))
    .sort((a, b) => b.balance - a.balance)

  return (
    <Panel title={title}>
      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>{kind === 'SUPPLIER' ? 'لا مورّدين بعد.' : 'لا موظّفين بعد.'}</Empty>
      ) : (
        <ul className="grid gap-1">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className="flex w-full items-baseline gap-2 rounded-lg border border-line px-3 py-2 text-sm hover:bg-surface-raised"
                onClick={() => onOpen({ kind, id: r.id })}
              >
                <span className="min-w-0 flex-1 truncate text-start">{r.name}</span>
                <span
                  className={`tabular shrink-0 font-bold ${r.balance > 0 ? 'text-warn' : r.balance < 0 ? 'text-ok' : 'text-ink-soft'}`}
                  dir="ltr"
                >
                  {formatMoney(Math.abs(r.balance))}
                </span>
                <span className="w-14 shrink-0 text-end text-xs text-ink-soft">
                  {r.balance > 0 ? 'علينا' : r.balance < 0 ? (kind === 'EMPLOYEE' ? 'سلفة' : 'له عندنا') : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-sm">
        المجموع: <b className="tabular" dir="ltr">{formatMoney(total)}</b>
      </p>
    </Panel>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-line p-3">
      <div className="text-xs text-ink-soft">{label}</div>
      <div className="tabular mt-1 text-xl font-bold" dir="ltr">{value}</div>
    </div>
  )
}
